import React, { useState, useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import ComponentsTable from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent/ComponentsTable'
import LastUpdatedText from 'components/LastUpdatedText'
import TabContentTitle from 'pages/HehmDevice/TabContentTitle'

import HehmDevice, { HehmDeviceEquipment, HehmDeviceHardware } from 'types/HehmDevice'

import ModemCard from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent/ModemCard'
import IndoorUnitCard from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent/IndoorUnitCard'
import OutdoorUnitCard from 'pages/HehmDevice/HehmDashboard/ConfigurationAndComponentsTabContent/OutdoorUnitCard'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Apptentive from 'helpers/apptentive'

import { color } from 'styles/colors'
import { PanelHeaderPrimaryButton, PanelHeaderSecondaryButton, screenSize } from 'styles/shared'
import { Panel } from 'components/Panel'

import MuiEditIcon from '@mui/icons-material/EditOutlined'
import Api from 'services/api'
import { useConfirmDialog, useAlertDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type Props = {
  hehmDevice: HehmDevice | null
  hehmDeviceHardware: HehmDeviceHardware | null
  hehmDeviceEquipment: HehmDeviceEquipment | null
  readOnly: boolean
  deviceId: string
  refreshPageCallback?: () => void | null | undefined
}

const ConfigurationAndComponentsTabContent = ({ hehmDevice, hehmDeviceHardware, hehmDeviceEquipment, readOnly, deviceId, refreshPageCallback }: Props) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [indoorConfig, setIndoorConfig] = useState<Partial<HehmDeviceEquipment['indoor']>>(
    hehmDeviceEquipment?.indoor ?? {}
  )
  const [outdoorConfig, setOutdoorConfig] = useState<Partial<HehmDeviceEquipment['outdoor']>>(
    hehmDeviceEquipment?.outdoor ?? {}
  )
  const [indoorHasErrors, setIndoorHasErrors] = useState(false)
  const [outdoorHasErrors, setOutdoorHasErrors] = useState(false)
  const { confirm } = useConfirmDialog()
  const { alert } = useAlertDialog()

  const hasChanges = useMemo(() => {
    const normalize = (val: unknown) => (val === null || val === undefined || val === '' ? null : val)

    const changed = (a: Record<string, unknown>, b?: Record<string, unknown> | null) =>
      Object.keys({ ...a, ...b }).some((k) => normalize(a[k]) !== normalize((b ?? {})[k]))

    return (
      changed(indoorConfig as Record<string, unknown>, hehmDeviceEquipment?.indoor as Record<string, unknown>) ||
      changed(outdoorConfig as Record<string, unknown>, hehmDeviceEquipment?.outdoor as Record<string, unknown>)
    )
  }, [indoorConfig, outdoorConfig, hehmDeviceEquipment])

  const hasErrors = indoorHasErrors || outdoorHasErrors

  const onSaveClick = useCallback(async () => {
    const isConfirmed = await confirm({
      title: CONFIRMATION_DIALOG_MESSAGES.SAVE_CONFIGURATION.TITLE,
      description: CONFIRMATION_DIALOG_MESSAGES.SAVE_CONFIGURATION.DESCRIPTION,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.YES,
      cancelText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.NO
    })
    if (!isConfirmed) return

    const modifiedEquipment: Partial<HehmDeviceEquipment> = {
      ...hehmDeviceEquipment,
      indoor: {
        ...hehmDeviceEquipment?.indoor,
        ...indoorConfig
      },
      outdoor: {
        ...hehmDeviceEquipment?.outdoor,
        ...outdoorConfig
      }
    }
    try {
      setIsSaving(true)
      await Api.HehmDevice.update(deviceId, modifiedEquipment)
      Apptentive.sendEvent('hehm_system_config_done')
      setIsEditing(false)
      await alert({ title: CONFIRMATION_DIALOG_MESSAGES.SUCCESS_MESSAGE.TITLE, description: CONFIRMATION_DIALOG_MESSAGES.SUCCESS_MESSAGE.DESCRIPTION })
      refreshPageCallback?.()
    } catch {
      await alert({ title: CONFIRMATION_DIALOG_MESSAGES.FAILURE_MESSAGE.TITLE, description: CONFIRMATION_DIALOG_MESSAGES.FAILURE_MESSAGE.DESCRIPTION })
    } finally {
      setIsSaving(false)
    }
  }, [deviceId, hehmDeviceEquipment, indoorConfig, outdoorConfig, confirm, alert, refreshPageCallback])

  const onCancelClick = useCallback(() => {
    setIndoorConfig(hehmDeviceEquipment?.indoor ?? {})
    setOutdoorConfig(hehmDeviceEquipment?.outdoor ?? {})
    setIsEditing(false)
  }, [hehmDeviceEquipment])

  useEffect(() => {
    if (!isEditing) {
      setIndoorConfig(hehmDeviceEquipment?.indoor ?? {})
      setOutdoorConfig(hehmDeviceEquipment?.outdoor ?? {})
    }
  }, [hehmDeviceEquipment, isEditing])

  return (
    <>
      <S.PanelHeaderLeft>
        <TabContentTitle>System Config</TabContentTitle>
        {!readOnly &&
          (!isEditing ? (
            <Button
              decoration="primary"
              styles={PanelHeaderSecondaryButton}
              onPress={() => setIsEditing(true)}
            >
              <S.ButtonText>
                <S.EditIcon />
                <span>Edit</span>
              </S.ButtonText>
            </Button>
          ) : (
            <>
              <Button
                decoration="secondary"
                styles={PanelHeaderSecondaryButton}
                text="Cancel"
                onPress={onCancelClick}
              />
              <Button
                decoration="positive"
                styles={PanelHeaderPrimaryButton}
                text={'Save'}
                onPress={onSaveClick}
                showSpinner={isSaving}
                enabled={hasChanges && !isSaving && !hasErrors}
              />
            </>
          ))}
      </S.PanelHeaderLeft>
      <LastUpdatedText timestamp={Date.now()} />

      <S.Cards>
        <ModemCard hehmDeviceHardware={hehmDeviceHardware} />
        <IndoorUnitCard
          hehmDeviceEquipment={hehmDeviceEquipment}
          isEditing={isEditing}
          onChangeCallback={(indoor) => setIndoorConfig((prev) => ({ ...prev, ...indoor }))}
          onValidationChange={setIndoorHasErrors}
        />
        <OutdoorUnitCard
          hehmDeviceEquipment={hehmDeviceEquipment}
          isEditing={isEditing}
          onChangeCallback={(outdoor) => setOutdoorConfig((prev) => ({ ...prev, ...outdoor }))}
          onValidationChange={setOutdoorHasErrors}
        />
      </S.Cards>

      <TabContentTitle>System Components (1)</TabContentTitle>
      <LastUpdatedText timestamp={Date.now()} />

      <ComponentsTable {...{ hehmDeviceHardware, hehmDevice }} />
    </>
  )
}

export default ConfigurationAndComponentsTabContent

const S = {
  Cards: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 20px;
    margin-bottom: 40px;

    @media ${screenSize.medium} {
      grid-template-columns: 1fr;
    }
  `,
  PanelHeaderLeft: styled(Panel.Header.Left)`
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  `,
  ButtonText: styled.div`
    color: ${color('primaryButton')};
    font-weight: 700;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 5px;
    &:hover {
      color: ${color('primaryButtonHover')};
    }
  `,
  EditIcon: styled(MuiEditIcon)`
    font-size: 1.25em;
  `
}
